import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl, ReactiveFormsModule} from '@angular/forms';
import { MatButton, MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatPaginator, MatPaginatorIntl, MatPaginatorModule } from '@angular/material/paginator';
import { MatSort, MatSortModule, Sort } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { Router } from '@angular/router';
import { User } from '../../models/user.model';
import { UsersService } from '../../services/users.service';
import { getPolishPaginatorIntl } from '../../polish-paginator-intl';
import { PermissionsService } from '../../services/permissions.service';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ItemType } from '../../types/item-type.type';
import { MatCardModule } from '@angular/material/card';
import { NotificationService } from '../../services/notification.service';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { ModalUserInvitationComponent } from '../modal-user-invitation/modal-user-invitation.component';
import { MatDialog } from '@angular/material/dialog';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize, Subject, takeUntil } from 'rxjs';
import { Role } from '../../models/role.model';


@Component({
    selector: 'app-users-list',
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        MatTableModule,
        MatPaginatorModule,
        MatSortModule,
        MatButtonToggleModule,
        MatIconModule,
        MatButtonModule,
        MatFormFieldModule,
        MatInputModule,
        MatTooltipModule,
        MatCardModule,
        MatAutocompleteModule,
    ],
    templateUrl: './users-list.component.html',
    styleUrl: './users-list.component.scss',
    providers: [{ provide: MatPaginatorIntl, useValue: getPolishPaginatorIntl() }]
})
export class UsersListComponent implements AfterViewInit, OnInit, OnDestroy {

  public viewTiles: boolean = true;

  public users: User[] = [];
  
  public filter: FormControl = new FormControl('')

  public roles: Role[] = [];

  public selectedUserType!: any;

  public displayedColumns: string[] = [
    'name',
    'email',
    'user_type',
    'account_status',
    'action'
  ];

  public dataSource = new MatTableDataSource<any>(this.users)  

  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  private destroy$ = new Subject<void>();

  constructor(
    private usersService: UsersService,
    readonly dialog: MatDialog,
    public router: Router,
    public permissionsService: PermissionsService,
    private notificationService: NotificationService,
  ) {

    const savedFilter = sessionStorage.getItem('userTableFilter') ?? "";
    

    this.filter.valueChanges
    .pipe(
      takeUntilDestroyed(),
      // finalize(() => console.log('UNSUBSCRIBE: filter'))
      )
    .subscribe(value => {
      this.dataSource.filter = value;
      sessionStorage.setItem('userTableFilter', value)
    });

    this.filter.setValue(savedFilter);
  }

  ngAfterViewInit() {
    setTimeout(() => {
      const savedPage = JSON.parse(sessionStorage.getItem('userListTablePage') || '{}');
      if(savedPage.pageIndex != null) {
        this.paginator.pageIndex = savedPage.pageIndex;
        this.paginator.pageSize = savedPage.pageSize;
      }

      const savedSort = JSON.parse(sessionStorage.getItem('userListTableSort') || '{}');
      if (savedSort.active) {
        this.sort.active = savedSort.active;
        this.sort.direction = savedSort.direction;
      }

      // Subskrypcja do zapisywania paginatora i sortowania
      this.paginator.page
      .pipe(
        takeUntil(this.destroy$),
        // finalize(() => console.log('savePaginator'))
      )
      .subscribe(page => {
        sessionStorage.setItem('userListTablePage', JSON.stringify({
          pageIndex: page.pageIndex,
          pageSize: page.pageSize
        }));
      });

      this.sort.sortChange
      .pipe(
        takeUntil(this.destroy$),
        // finalize(() => console.log('saveSort'))
      )
      .subscribe((sort: Sort) => {
        sessionStorage.setItem('userListTableSort', JSON.stringify(sort));
      });
    });
  }

  ngOnInit(): void {

    this.usersService.getRoles().subscribe({
      next: (response: any) => {
        console.log("getRoles response", response)
        this.roles = response.rolesList;
      },
      error: error => {
        console.log("getRoles error", error)
        if (error.status === 401) {
          this.router.navigate(['/']);
        }
      }
    })

    // this.roles = this.usersService.getUserTypes();
    this.fetchUsers();
  }

  ngOnDestroy(): void {
    // Odsubskrybowanie paginatora i sortowania
    this.destroy$.next();
    this.destroy$.complete();
  }

  fetchUsers(): void {
    this.usersService.fetchUsers()
    .pipe(finalize(() => console.log('fetchUsers')))
      .subscribe({
        next: (response: any) => {
          this.users = response.users.map((el: User) => {
            //console.log(el)
            let newEl = el
            newEl.namedType = (el.is_admin === 0) ? "USER" : "ADMIN"
            return newEl
          })
          
          this.dataSource.data = this.users;
          this.dataSource.paginator = this.paginator;
          this.dataSource.sort = this.sort;

          this.dataSource.filterPredicate = (data: User, filter: string) => {
            const fullname = (data.name + ' ' + data.surname).toLowerCase()
            
            return fullname.includes(filter) || data.email.toLowerCase().includes(filter)
          };

        },
        error: error => {
          console.log("fetchUsers error", error)
          if (error.status === 401) {
            this.router.navigate(['/']);
          }
        }

      });
      
  }

  createUser() {
    let newUser = new User(0, "", "", "", 0, "", "", new Role(0, "", "", 0));
    return newUser;
  }

  doFilter(value: string) {
    this.dataSource.filter = value.trim().toLowerCase()
  }

  deleteElement(user: User) {
    if (confirm("Czy na pewno usunąć " + user.name + "?")) {
      this.usersService.deleteUser(user)
        .subscribe( {
          next: (response: any) => {
            console.log(response);
            this.notificationService.pushNotification(
              `Użytkownik został usunięty`, 'info', 5000);
            this.fetchUsers();
          },
          error: error => {
            console.log("error subscribe deleteElement ", error);
            const eRR = error.error.message

            alert("Error: " + error.error.message);
          }

        });
    }
  }

  openModal() {
     const dialogRef = this.dialog.open(ModalUserInvitationComponent, {
      height: '300px', 
      position: {
        top: '5%'
      },
      data: {
        roles: this.roles,
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        console.log('Dialog closed with result:', result);
      } else {
        console.log('Dialog was dismissed');
      }
    });
  };

  openInvitationList() {
    this.router.navigate(['/invitation-list']);
  }

  addUser() {
    this.router.navigate(['/users/create']);
  }

  editUser(user: User) {
    this.usersService.setCurrentUser(user);
    this.router.navigate(['/users/edit/' + user.id]);
  }

  clearFilter() {
    this.filter.setValue('');

    sessionStorage.removeItem('userTableFilter');
    sessionStorage.removeItem('userListTableSort');
    sessionStorage.removeItem('userListTablePage');
  }
}
